/**
 * PipelineGraphView — visualization of a pipeline's workflow graph, with
 * optional live per-node editing.
 *
 * Presentational by default. The parent passes the backend serialized graph
 * (`{ nodes, edges, nodeConfigs, stateSchema }`); this component converts it to
 * React Flow shape (with elkjs auto-layout) via the shared
 * `convertBackendGraphToReactFlow` util and renders it inside a fixed-height
 * container using the existing `GraphFlowEditor`. The graph structure is always
 * locked (no drag/add/delete) — `readonly`.
 *
 * Node detail on click: handled by GraphFlowEditor itself — it opens the
 * existing NodeConfigModal (the Monaco-based "IDE" node modal). We do NOT roll
 * our own detail panel.
 *
 * Live editing: when `workflowUuid` is supplied (the deployed-pipeline Overview),
 * the modal opens EDITABLE. Saving a node diffs the edited config against the
 * node's current effective config and PATCHes only the delta to
 * `PATCH /workflows/{uuid}` as `nodeConfigOverrides[nodeId]`, so a few extra
 * instruction lines live-update the deployed pipeline without re-deploying.
 * Without `workflowUuid` (e.g. the marketplace preview) it stays view-only.
 *
 * Defensive: a falsy graph or one with no nodes renders a clean empty state
 * instead of crashing.
 */
import React, {
  useEffect, useState, useCallback, useMemo, useRef, useImperativeHandle, forwardRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { scanOutline, contractOutline } from 'ionicons/icons';
import {
  GraphFlowEditor,
  convertBackendGraphToReactFlow,
} from '../../graph-workflow-editor';
import { ModelNode } from '../../graph-workflow-editor/molecules';
import { withModelSatellites } from '../../graph-workflow-editor/utils/modelSatellites';
import AgentModelModal from '../../molecules/AgentModelModal/AgentModelModal';
import {
  useAgentOptions, findAgentOptionValue,
  filterOptionsByAgents, firstOptionForAgent,
} from '../../../utils/agent-options';
import {
  VENDOR_ENV_KEYS, agentSnapshotFor, projectSecretFor, unreadyNodes,
} from '../../../utils/nodeModel';
import {
  dispatchedSlugs, envKeyMap, childIsReady,
} from '../../../utils/childAgentReadiness';
import api from '../../../services/api';
import './PipelineGraphView.css';

const EMPTY_FLOW = { nodes: [], edges: [], nodeConfigs: {}, stateSchema: null };
const EMPTY_FLAGGED = [];
const EMPTY_CHILD_READY = {};
const MODEL_NODE_TYPES = { modelNode: ModelNode };

// Keys a node override may carry (mirrors NodeConfigModal.handleSave's output).
const OVERRIDE_KEYS = ['prompt', 'extraPromptInstructions', 'tools', 'executeCode'];
const norm = (v) => (v === undefined ? null : v);

// Delta of `config` vs the node's current effective config. Only changed keys
// are sent so we never freeze a node's other (template-tracking) fields. Returns
// `{}` when nothing changed.
const overrideDelta = (config, base) => {
  const out = {};
  for (const k of OVERRIDE_KEYS) {
    if (JSON.stringify(norm(config[k])) !== JSON.stringify(norm(base?.[k]))) {
      out[k] = norm(config[k]);
    }
  }
  return out;
};

// `projectId` (optional): lets the node modal resolve a sub-workflow node's
// CHILD agent (deployed in the same project) and embed its graph.
// `embedded` (optional): render as a non-interactive embed (inside the node
// modal's sub-workflow panel) — node clicks disabled so modals never recurse,
// fullscreen button hidden. Pan/zoom stay enabled.
// `nodeConfigOverrides` (optional): the workflow row's RAW per-node override
// map. Load-bearing for the model disc: the `graph` this component receives has
// those overrides already MERGED into nodeConfigs, so `cfg.agent` alone cannot
// tell a vendor the TEMPLATE pinned from one the OPERATOR just picked — and
// reading it blind renders every just-set node as fixed-in-code, locked and
// unclickable. Absent → discs render, but a user's own pick reads as a lock.
const PipelineGraphView = forwardRef(({ graph, workflowUuid, webhookEnabled = false, onSaved, setToast, projectId = null, embedded = false, nodeConfigOverrides = null, modelBlocks = null }, ref) => {
  const canvasRef = useRef(null);
  const [flow, setFlow] = useState(EMPTY_FLOW);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // The deployed agent's cron schedule (null = none). Fetched separately from
  // GET /workflows/{uuid}/schedule — the workflow detail object doesn't carry
  // it. Drives the Start node's trigger badge alongside `webhookEnabled`.
  const [schedule, setSchedule] = useState(null);
  // Bumped on every fullscreen toggle so the graph re-centers + zooms to fit
  // the resized canvas.
  const [fitSignal, setFitSignal] = useState(0);

  const hasGraph = !!(graph && Array.isArray(graph.nodes) && graph.nodes.length > 0);
  const editable = !!workflowUuid;
  // Model discs only on the deployed-agent surface: a marketplace preview has
  // no agent to hang a credential on, and the embedded child graph must not
  // offer edits that belong on the child's own page.
  const showModels = editable && !embedded;

  // Esc exits fullscreen; re-fit the view whenever the mode TOGGLES (skip the
  // initial mount so DefaultZoomOut owns first-load framing).
  const didMount = React.useRef(false);
  useEffect(() => {
    if (didMount.current) {
      setFitSignal((n) => n + 1);
    } else {
      didMount.current = true;
    }
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  // Persist a single node's edits as a live override on the deployed pipeline.
  const handleSaveNodeConfig = useCallback(async (nodeId, config, baseConfig) => {
    if (!workflowUuid) return;
    const delta = overrideDelta(config, baseConfig);
    if (Object.keys(delta).length === 0) return; // nothing changed
    try {
      await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, {
        nodeConfigOverrides: { [nodeId]: delta },
      });
      if (setToast) setToast({ show: true, message: 'Node updated — live on the deployed pipeline.', color: 'success' });
      if (onSaved) onSaved();
    } catch (err) {
      if (setToast) setToast({ show: true, message: `Failed to save node: ${err?.message || 'unknown error'}`, color: 'danger' });
    }
  }, [workflowUuid, onSaved, setToast]);

  // Fetch the deployed agent's cron schedule so the Start node can show a
  // trigger badge. Tied to workflowUuid; 404 = no schedule (a valid state).
  // Best-effort: a failure just means no cron badge, never blocks the graph.
  useEffect(() => {
    if (!workflowUuid) { setSchedule(null); return undefined; }
    let cancelled = false;
    api.get(`/workflows/${workflowUuid}/schedule`)
      .then((data) => { if (!cancelled) setSchedule(data?.schedule || null); })
      .catch(() => { if (!cancelled) setSchedule(null); });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // ── Per-node model ────────────────────────────────────────────────────
  // Every node that runs a model carries a disc showing WHICH model, and
  // clicking it picks the vendor + model + key. The two credential sources the
  // runtime consults, in ITS order: this agent's own env snapshot first, the
  // project's shared credential second.
  const { options: agentOptions } = useAgentOptions();
  const [agentEnvKeys, setAgentEnvKeys] = useState(null);   // {ENV_NAME: lastFour|true}
  const [project, setProject] = useState(null);
  const [picker, setPicker] = useState(null);               // {nodeId, label} | null
  // Node ids the Trigger check last pointed at. Held here (not derived) so the
  // ring appears only when the operator ASKS to run — an agent mid-setup
  // shouldn't be shouting at them the whole time they configure it.
  const [flagged, setFlagged] = useState(EMPTY_FLAGGED);
  // slug → true | false | null(unknown). A dispatch node runs ANOTHER agent,
  // and that agent dying for want of a model kills this run too, so the check
  // follows the dispatch edges. An unknown child is never blamed.
  const [childReady, setChildReady] = useState(EMPTY_CHILD_READY);
  const navigate = useNavigate();

  const loadAgentEnv = useCallback(async () => {
    if (!workflowUuid) { setAgentEnvKeys(null); return; }
    try {
      const data = await api.get(`/workflows/${encodeURIComponent(workflowUuid)}/env`);
      const map = {};
      for (const e of (data?.entries || [])) map[e.key] = e.lastFour || true;
      for (const k of (data?.keys || [])) if (!map[k]) map[k] = true;
      setAgentEnvKeys(map);
    } catch {
      // Unknown ≠ missing. Leaving this null makes vendorHasKey abstain rather
      // than paint every disc red on a transient failure.
      setAgentEnvKeys(null);
    }
  }, [workflowUuid]);

  useEffect(() => { loadAgentEnv(); }, [loadAgentEnv]);

  const loadProject = useCallback(async () => {
    if (!projectId) { setProject(null); return; }
    try {
      const data = await api.get(`/projects/${encodeURIComponent(projectId)}`);
      setProject(data?.project || data || null);
    } catch {
      setProject(null);
    }
  }, [projectId]);

  useEffect(() => { loadProject(); }, [loadProject]);

  const projectSecrets = project?.secrets || null;
  const projectVendor = project?.aiAgent || '';

  // Sub-agent readiness. Deliberately waits on `project` — the credentials it
  // judges by arrive asynchronously, and answering from an empty map would
  // call every child keyless. Each child is read from its OWN row + OWN env,
  // because that is where its keys live.
  useEffect(() => {
    if (!showModels || !projectId || !hasGraph) return undefined;
    if (project === null) return undefined;              // creds not in yet
    const slugs = dispatchedSlugs(graph);
    if (slugs.length === 0) { setChildReady(EMPTY_CHILD_READY); return undefined; }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(slugs.map(async (slug) => {
        try {
          const row = await api.get(
            `/projects/${encodeURIComponent(projectId)}/workflows/${encodeURIComponent(slug)}`,
          );
          if (!row || row.notInstalled === true) return [slug, null];
          let env = null;
          if (row.uuid) {
            env = await api.get(`/workflows/${encodeURIComponent(row.uuid)}/env`)
              .then(envKeyMap)
              .catch(() => null);
          }
          return [slug, childIsReady(row, env, projectSecrets, projectVendor)];
        } catch {
          return [slug, null];                            // unreadable → unjudged
        }
      }));
      if (!cancelled) setChildReady(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [showModels, projectId, hasGraph, graph, project, projectSecrets, projectVendor]);

  // Persist one node's model pick. Order matters: the CREDENTIAL lands before
  // the pick, so we can never leave a node pointed at a vendor whose key never
  // arrived — exactly the broken state this picker exists to prevent.
  const handleSaveNodeModel = useCallback(async (nodeId, body, locked = false) => {
    const vendor = typeof body.aiAgent === 'string' ? body.aiAgent : '';
    const aiModel = typeof body.aiModel === 'string' ? body.aiModel : '';
    const fresh = (typeof body.aiOAuthToken === 'string' && body.aiOAuthToken)
      || (typeof body.aiApiKey === 'string' && body.aiApiKey)
      || '';

    // 1. The project's shared credential — only when the user opted IN.
    if (fresh && body.saveToProject && projectId) {
      const secrets = {};
      if (vendor === 'codex') secrets.codexApiKey = fresh;
      else if (vendor === 'gemini') secrets.geminiApiKey = fresh;
      else if (/^sk-ant-oat01-/.test(fresh)) { secrets.aiOAuthToken = fresh; secrets.aiApiKey = ''; }
      else { secrets.aiApiKey = fresh; secrets.aiOAuthToken = ''; }
      await api.put(`/projects/${encodeURIComponent(projectId)}`, { secrets });
    }

    // 2. THIS agent's own env — ALWAYS, whatever the toggle said. The runtime
    //    reads the agent env before the project credential, so the snapshot is
    //    what keeps the node running if the project credential is later
    //    removed. Not swallowed: with the toggle off this is the only place
    //    the key lands, and a green toast over a key that went nowhere is the
    //    worst outcome available.
    if (fresh && vendor) {
      const envKey = /^sk-ant-oat01-/.test(fresh)
        ? 'CLAUDE_CODE_OAUTH_TOKEN'
        : (VENDOR_ENV_KEYS[vendor] || [])[0];
      if (envKey) {
        await api.patch(
          `/workflows/${encodeURIComponent(workflowUuid)}/env/${encodeURIComponent(envKey)}`,
          { value: fresh },
        );
        // …and CLEAR the vendor's OTHER credential slots, or the new key can be
        // silently ignored. `enforceSingleClaudeCredential` makes OAuth WIN: a
        // stale CLAUDE_CODE_OAUTH_TOKEN causes the run to DROP the fresh
        // ANTHROPIC_API_KEY we just wrote — so replacing a dead OAuth token
        // with a working API key would show green here and keep running on the
        // dead one. One credential per vendor, the same rule the project
        // secrets path already follows (`aiOAuthToken` / `aiApiKey` clear each
        // other). Best-effort: the key that matters has already landed.
        for (const stale of (VENDOR_ENV_KEYS[vendor] || [])) {
          if (stale === envKey) continue;
          try {
            await api.delete(`/workflows/${encodeURIComponent(workflowUuid)}/env/${encodeURIComponent(stale)}`);
          } catch { /* absent → nothing to clear, which is the desired state */ }
        }
      }
    }

    // 3. The pick itself → the same per-node override map custom prompts use.
    //    Live: it takes effect on the next run, with no redeploy.
    //
    //    SKIPPED for a template-fixed node: there was no pick to make, and
    //    writing one would be worse than pointless — an override the engine
    //    ranks ABOVE the code that is actually choosing the model, so the
    //    graph would start claiming a model the run never uses.
    if (!locked) {
      await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, {
        nodeConfigOverrides: { [nodeId]: { agent: vendor || null, model: aiModel || 'auto' } },
      });
    }

    // This node is no longer what the Trigger check was complaining about, so
    // stop ringing it. Only THIS one — any other node still unset stays lit, or
    // clearing them all would read as "everything's fine now" after fixing one
    // of three. (The flag is our own marker, not derived state: without this it
    // glowed forever, over a node the operator had already dealt with.)
    setFlagged((prev) => {
      if (!prev.includes(nodeId)) return prev;
      const next = prev.filter((id) => id !== nodeId);
      return next.length ? next : EMPTY_FLAGGED;
    });

    await loadAgentEnv();
    if (body.saveToProject) await loadProject();
  }, [workflowUuid, projectId, loadAgentEnv, loadProject]);

  // The Trigger check. Deliberately NOT a disabled button: a dead control tells
  // you nothing about WHY. The button stays live and answers by scrolling to
  // the graph, ringing every node that can't run, and naming them in a toast —
  // and each of those rings is the very control that fixes it.
  //
  // Returns true when it BLOCKED (the caller must not open the trigger modal).
  const flagUnreadyModels = useCallback(() => {
    if (!showModels || !hasGraph) return false;
    const bad = unreadyNodes({
      graph,
      overrides: nodeConfigOverrides,
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
    });
    // …and the sub-agents this graph dispatches: a child dying for want of a
    // model kills the parent's run too.
    const badChildren = Object.keys(childReady).filter((slug) => childReady[slug] === false);
    if (bad.length === 0 && badChildren.length === 0) {
      setFlagged(EMPTY_FLAGGED);
      return false;
    }
    setFlagged(bad.map((b) => b.id));
    canvasRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Short on purpose. The glowing discs already say WHERE — repeating every
    // node id and its reason in the toast made a line too long to read, for
    // information the graph is showing better. Name the one node when there IS
    // one; otherwise just count them.
    const noKey = bad.filter((b) => b.why === 'no key').length;
    setToast?.({
      show: true,
      isOpen: true,
      color: 'warning',
      message: bad.length === 0
        ? `Finish ${badChildren.join(', ')} first — open the red sub-agent.`
        : bad.length === 1
          ? `${bad[0].id} needs ${noKey ? 'a key' : 'a model'} — click it on the graph.`
          : `${bad.length} nodes need a model — click each on the graph.`,
    });
    return true;
  }, [showModels, hasGraph, graph, nodeConfigOverrides, agentEnvKeys,
    projectSecrets, projectVendor, modelBlocks, childReady, setToast]);

  useImperativeHandle(ref, () => ({ flagUnreadyModels }), [flagUnreadyModels]);

  // Normalized trigger summary injected into the Start node's data.
  const triggers = useMemo(() => ({
    cron: schedule?.cron || null,
    timezone: schedule?.timezone || null,
    webhook: !!webhookEnabled,
  }), [schedule, webhookEnabled]);

  // Inject the trigger summary into the Start terminal node so TerminalNode can
  // render the badge. Recomputes when the schedule resolves (badge appears once
  // the async fetch lands; syncFromProps re-syncs GraphFlowEditor).
  const nodesWithTriggers = useMemo(() => {
    if (!Array.isArray(flow.nodes)) return flow.nodes;
    return flow.nodes.map((n) => {
      if (n.type === 'terminal' && n.data?.terminal === 'start') {
        return { ...n, data: { ...n.data, triggers } };
      }
      const slug = n.data?.dispatchesWorkflow;
      if (!slug || !showModels) return n;
      // A dispatch node is a POINTER to another agent, and that agent's row
      // owns its own nodes' models. Editing them from here would mean writing
      // a different row from this page, so we go THERE instead of opening a
      // read-only mirror of a graph you cannot act on.
      const ready = childReady[slug];
      return {
        ...n,
        data: {
          ...n.data,
          notReady: ready === false,
          onNodeClick: () => navigate(`/projects/${encodeURIComponent(projectId)}/agents/${encodeURIComponent(slug)}`),
        },
      };
    });
  }, [flow.nodes, triggers, showModels, childReady, projectId, navigate]);

  // The graph the editor actually renders: the flow plus one model disc per
  // model-running node. Only on the deployed-agent surface — a marketplace
  // preview has no agent to hang a credential on, and the embedded child graph
  // must not offer edits that belong on the child's own page.
  const withModels = useMemo(() => {
    if (!showModels) return { nodes: nodesWithTriggers, edges: flow.edges };
    return withModelSatellites(nodesWithTriggers, flow.edges, graph, {
      overrides: nodeConfigOverrides,
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
      flagged,
      onPick: (nodeId, label, state) => setPicker({ nodeId, label, state }),
    });
  }, [showModels, nodesWithTriggers, flow.edges, graph, nodeConfigOverrides,
    agentEnvKeys, projectSecrets, projectVendor, modelBlocks, flagged]);

  // Seed the picker with what the node runs TODAY, and show the credential we
  // would actually use: this agent's own snapshot first (masked to its last
  // four), else the project's. So a saved key reads back as saved instead of
  // asking again.
  // A template-FIXED node opens the same modal with the model read-only: the
  // template chose what runs, so there is nothing to pick — but it cannot
  // supply the credential, so the key field is exactly as live as anywhere
  // else. Without this the node was a dead end: not clickable, and skipped by
  // the trigger check, so a fixed-model agent could never be given its key.
  const isLockedPick = picker?.state?.kind === 'locked';

  const pickerSeed = useMemo(() => {
    if (!picker) return { value: '', options: [], keysByAgent: {}, vendorsWithProjectKey: [] };
    const ov = nodeConfigOverrides?.[picker.nodeId] || {};
    const keysByAgent = {};
    const vendorsWithProjectKey = [];
    for (const v of Object.keys(VENDOR_ENV_KEYS)) {
      const snap = agentSnapshotFor(v, agentEnvKeys);
      const proj = projectSecretFor(v, projectSecrets, projectVendor);
      if (proj) vendorsWithProjectKey.push(v);
      // A mask (contains '...') is what tells the field "a key is stored —
      // leave me blank to keep it". `true` means we know a key exists but not
      // its tail, so render a tail-less mask rather than an empty field.
      if (snap) keysByAgent[v] = typeof snap === 'string' ? `sk-...${snap}` : 'sk-...';
      else if (proj) keysByAgent[v] = proj;
    }
    // NO "Project default" sentinel. That option means "this row picks no
    // vendor" — and a node with no vendor has no credential field either
    // (`needsKey = Boolean(vendor)`), so opening on it showed a model dropdown
    // with nowhere to put the key. There IS no agent-wide default any more:
    // every node lands on a concrete vendor, so the picker only offers those.
    const options = filterOptionsByAgents(agentOptions, []);
    // Open on what the node runs today; failing that, the project's vendor;
    // failing that, the newest option there is. Never on nothing.
    const value = findAgentOptionValue({ aiAgent: ov.agent, aiModel: ov.model }, options)
      || (projectVendor ? firstOptionForAgent(options, projectVendor) : '')
      || (options[0]?.value || '');
    // Fixed node: the vendor is whatever will actually run, and the model is
    // whatever the disc already resolved — not a dropdown value.
    if (picker.state?.kind === 'locked') {
      return {
        value: picker.state.vendor ? `${picker.state.vendor}:auto` : '',
        options,
        keysByAgent,
        vendorsWithProjectKey,
      };
    }
    return { value, options, keysByAgent, vendorsWithProjectKey };
  }, [picker, nodeConfigOverrides, agentEnvKeys, projectSecrets, projectVendor, agentOptions]);

  // Convert backend graph → React Flow shape (async: elkjs auto-layout).
  // Guarded against out-of-order resolutions if `graph` changes mid-flight.
  useEffect(() => {
    let cancelled = false;
    if (!hasGraph) {
      setFlow(EMPTY_FLOW);
      return undefined;
    }
    setLoading(true);
    convertBackendGraphToReactFlow(graph)
      .then((result) => {
        if (!cancelled) setFlow(result || EMPTY_FLOW);
      })
      .catch(() => {
        if (!cancelled) setFlow(EMPTY_FLOW);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graph, hasGraph]);

  if (!hasGraph) {
    return (
      <div className="pipeline-graph-view pipeline-graph-view--empty">
        <div className="pipeline-graph-view__empty-text">
          Graph not available for this pipeline.
        </div>
      </div>
    );
  }

  return (
    <div className={`pipeline-graph-view${fullscreen ? ' pipeline-graph-view--fullscreen' : ''}${embedded ? ' pipeline-graph-view--embedded' : ''}`}>
      <div className="pipeline-graph-view__canvas" ref={canvasRef}>
        {loading && (
          <div className="pipeline-graph-view__loading">Laying out graph…</div>
        )}
        {!embedded && (
          <button
            type="button"
            className="pipeline-graph-view__fs-btn"
            onClick={() => setFullscreen((v) => !v)}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            data-tooltip={fullscreen ? 'Exit full screen' : 'Full screen'}
          >
            <IonIcon icon={fullscreen ? contractOutline : scanOutline} />
          </button>
        )}
        <GraphFlowEditor
          readonly
          syncFromProps
          customNodeTypes={MODEL_NODE_TYPES}
          initialNodes={withModels.nodes}
          initialEdges={withModels.edges}
          nodeConfigs={flow.nodeConfigs}
          stateSchema={flow.stateSchema}
          showResetPanel={false}
          showGraphInfoPanel={false}
          fitSignal={fitSignal}
          nodeConfigEditable={editable}
          onSaveNodeConfig={editable ? handleSaveNodeConfig : null}
          projectId={projectId}
          nodesClickable={!embedded}
        />
      </div>

      {picker && (
        <AgentModelModal
          isOpen
          onClose={() => setPicker(null)}
          variant="pipeline"
          title={`${picker.label} — ${isLockedPick ? 'key' : 'model'}`}
          // The vendor is already named by the key field's own label right
          // below ("Anthropic API key"), so repeating it here just made the
          // line longer. Show it only when the model itself is unknowable and
          // the vendor is the only thing we can say.
          fixedModelLabel={isLockedPick
            ? (picker.state.label === 'Fixed' && picker.state.vendor
              ? `Fixed · ${picker.state.vendor}`
              : picker.state.label)
            : ''}
          agentOptions={pickerSeed.options}
          seedValue={pickerSeed.value}
          seedKeysByAgent={pickerSeed.keysByAgent}
          // The opt-in appears ONLY for a vendor the project has no credential
          // for — there is nothing to ask once the project already has one —
          // and it is never pre-checked: writing a key into shared project
          // settings is the user's call, not our default.
          vendorsWithProjectKey={pickerSeed.vendorsWithProjectKey}
          saveToProjectLabel="Also save as this project's default key"
          onSave={(body) => handleSaveNodeModel(picker.nodeId, body, isLockedPick)}
          onSaved={onSaved}
          setToast={setToast}
        />
      )}
    </div>
  );
});

export default PipelineGraphView;
